import { Control, useController } from "react-hook-form";
import type { CheckoutFormData } from "./checkout.types";
import RadioInputBar from "./radio-input-bar";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { ShippingMethod } from "@/networking/shipping.api";
import { CartResponse } from "@/networking/carts.api";
import { useEffect } from "react";

function Loader() {
  return (
    <div className="mt-2 w-full animate-pulse space-y-4">
      <div className="h-12 w-full rounded bg-gray-200"></div>
      <div className="h-12 w-full rounded bg-gray-200"></div>
      <div className="h-12 w-full rounded bg-gray-200"></div>
    </div>
  );
}

export default function ShippingMethodSelect({
  control,
  isLoading,
  showLabel = true,
  onSelect,
  options,
  networkError,
  setNetworkError,
  addressId,
  selectedShippingMethod,
  isShippingMethodMutationPending = false,
}: {
  control: Control<CheckoutFormData>;
  isLoading: boolean;
  showLabel?: boolean;
  onSelect: (shippingMethod: ShippingMethod) => void;
  options: ShippingMethod[];
  networkError?: string | undefined;
  setNetworkError?: (error: string | undefined) => void;
  addressId: number | undefined;
  selectedShippingMethod: CartResponse["cart"]["shipping_method"] | undefined;
  isShippingMethodMutationPending?: boolean;
}) {
  const { t } = useTranslation();

  const {
    field,
    fieldState: { error },
  } = useController({
    name: "customer.shipping_method.id",
    control,
  });

  useEffect(() => {
    if (selectedShippingMethod) {
      field.onChange(selectedShippingMethod.id);
    }
  }, [selectedShippingMethod, field]);

  return (
    <fieldset className={cn(showLabel && "mt-6")}>
      {showLabel && (
        <legend className="block text-sm/6 font-medium text-gray-900">
          {t("shipping_method")}
        </legend>
      )}
      {error && <div className="text-sm text-red-500">{error.message}</div>}
      {networkError && (
        <div className="text-sm text-red-500">{networkError}</div>
      )}
      {isLoading ? (
        <Loader />
      ) : options.length > 0 ? (
        <div className="mt-2 flex flex-col">
          {options?.map((option, index) => {
            const isSelected = option.id === field.value;
            const isFirst = index === 0;
            const isLast = index === options.length - 1;

            // Determine border radius classes
            let borderRadiusClass = "";
            if (isFirst && isLast) {
              borderRadiusClass = "rounded";
            } else if (isFirst) {
              borderRadiusClass = "rounded-t";
            } else if (isLast) {
              borderRadiusClass = "rounded-b";
            } else {
              borderRadiusClass = "rounded-none";
            }

            // Adjust border to remove duplicate borders between items
            let borderClass = "";
            if (!isFirst) {
              borderClass = "-mt-1px"; // Overlap border with previous item
            }

            return (
              <div key={option.id} className={borderClass}>
                <RadioInputBar
                  leftContent={<div>{option.title}</div>}
                  name={field.name}
                  rightContent={
                    option.price_label ? (
                      <div className="text-sm font-semibold text-gray-900">
                        {option.price_label}
                      </div>
                    ) : null
                  }
                  onSelect={() => {
                    if (isShippingMethodMutationPending) return;

                    field.onChange(option.id);
                    onSelect(option);
                    setNetworkError?.(undefined);
                  }}
                  isSelected={isSelected}
                  disabled={isShippingMethodMutationPending}
                  className={`${borderRadiusClass} ${
                    isSelected ? "bg-white" : "bg-gray-100/40"
                  } ${
                    isShippingMethodMutationPending
                      ? "cursor-not-allowed opacity-50"
                      : ""
                  }`}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="min-h-input mt-2 flex w-full flex-row items-center justify-center space-x-2 rounded bg-gray-100/40 px-4 text-sm text-gray-500 shadow-xs">
          {options.length === 0 && !addressId ? (
            <div>{t("enter_shipping_address_to_see_methods")}</div>
          ) : (
            <div>{t("no_shipping_options_available")}</div>
          )}
        </div>
      )}
    </fieldset>
  );
}
